import { HttpClient } from '@angular/common/http';
import { effect, inject, Injectable, signal, untracked } from '@angular/core';
import { form } from '@angular/forms/signals';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, map, Observable, of, tap, throwError } from 'rxjs';

import {
  ExperienceAnalyteResponseV1,
  ExperienceCategoryResponseV1,
  ExperienceResponseV1
} from '@core/models/experience';
import { HealthHistoryQuestionnaire } from '@core/models/health-history/health-history';
import { HealthHistoryQuestion, healthHistoryQuestions } from '@core/models/health-history/question';
import { HealthHistorySection, healthHistorySections } from '@core/models/health-history/section';
import { HealthProfile } from '@core/models/health-profile';

import { GroupService } from '../group/group';
import { LogService } from '../log/log';
import { ToastService } from '../toast/toast';
import { mockExperience1, mockExperienceCategoryDetails, mockHealthProfile1 } from './health-mock';

@Injectable({
  providedIn: 'root'
})
export class HealthService {
  private readonly groupService = inject(GroupService);
  private readonly http = inject(HttpClient);
  private readonly logService = inject(LogService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  // private readonly healthApiUrl = `${environment.apiUrl}${environment.apis.healthEngine}/v1`;

  private readonly viewingUser = this.groupService.viewingUser;

  private readonly healthProfileMap$ = signal<{ [userId: string]: HealthProfile | undefined }>({});
  readonly healthProfileMap = this.healthProfileMap$.asReadonly();
  private readonly healthHistorySections$ = signal<HealthHistorySection[] | null>(healthHistorySections);
  readonly healthHistorySections = this.healthHistorySections$.asReadonly();
  private readonly healthHistoryQuestions$ = signal<HealthHistoryQuestion[] | null>(healthHistoryQuestions);
  readonly healthHistoryQuestions = this.healthHistoryQuestions$.asReadonly();

  private readonly healthHistoryFormModel$ = signal<HealthHistoryQuestionnaire>({
    birthday: null,
    geneticSex: null,
    pregnantLactating: null,
    height: null,
    weight: null,
    waistCircumference: null,
    dailyActivityLevel: null,
    satisfiedWithWeight: null,
    ethnicity: null,
    feltEnergyLevel: null,
    feltPain: null,
    feltHappiness: null,
    feltThinking: null,
    feltContentment: null,
    feltMemory: null,
    feltEating: null,
    feltSleep: null,
    feltBodyShape: null,
    feltTummy: null,
    feltBowelMovement: null,
    feltPurpose: null,
    feltSelfCare: null,
    feltRelationships: null,
    feltSpiritually: null,
    feltMotivation: null,
    physicalActivityTimeSpent: null,
    physicalActivityType: [],
    hoursOfSleep: null,
    glassesOfWater: null,
    stressWork: null,
    stressFamily: null,
    stressSocial: null,
    stressFinancial: null,
    stressHealth: null,
    stressOther: null,
    eatOut: null,
    dietIsHealthy: null,
    eatBreakfast: null,
    eatHealthySnacks: null,
    neverEatLate: null,
    adoptHealthierLifestyle: null,
    stressImpactAppetite: null,
    foodCravings: null,
    feelEnergized: null,
    sugaryDrinks: null,
    alcoholUse: null,
    tobaccoUse: null,
    drugUse: null,
    toxinConcern: null,
    healthConcernPhysicalActivity: null,
    healthConcernNutrition: null,
    healthConcernOverweight: null,
    healthConcernTobacco: null,
    healthConcernSubstanceAbuse: null,
    healthConcernMentalHealth: null,
    healthConcernInjuryViolence: null,
    healthConcernEnvironmentalQuality: null,
    healthConcernImmunization: null,
    healthConcernAccessToHealthcare: null,
    howOftenSick: null,
    enoughEnergyForDailyActivities: null,
    motivationForGoals: null,
    dietaryGoals: [],
    medications: [],
    supplements: [],
    conditionAutoImmune: null,
    conditionBowelOstomy: null,
    conditionCancer: null,
    conditionCeliac: null,
    conditionCopd: null,
    conditionChf: null,
    conditionConstipation: null,
    conditionCoronaryArtery: null,
    conditionCrohns: null,
    conditionCysticFibrosis: null,
    conditionDimentiaAlzeimerParkinson: null,
    conditionDiabetes: null,
    conditionDiarrhea: null,
    conditionDiverticulitis: null,
    conditionEatingDisorder: null,
    conditionGerd: null,
    conditionGastroparesis: null,
    conditionGout: null,
    conditionHighBloodPressure: null,
    conditionHighCholesterol: null,
    conditionHivImmuneSuppression: null,
    conditionIbs: null,
    conditionKidneyDisease: null,
    conditionOrganTransplant: null,
    conditionOsteoporosis: null,
    conditionOverweightObesity: null,
    conditionPancreatitis: null,
    conditionPepticUlcer: null,
    conditionTroubleChewingSwallowing: null,
    conditionOther: null,
    conditionNone: null,
    prescribedDiet: null,
    prescribedDietDescription: null,
    allergens: [],
    intolerances: []
  });
  readonly healthHistoryForm = form(this.healthHistoryFormModel$);

  // Snapshot of the form as it stood when a section was entered, so backing out of a section can
  // discard unsaved edits and restore the values the user actually has.
  private readonly healthHistorySnapshot$ = signal<HealthHistoryQuestionnaire | null>(null);

  private readonly experiencesMap$ = signal<{ [experienceId: string]: ExperienceResponseV1 | undefined }>({});
  readonly experiencesMap = this.experiencesMap$.asReadonly();
  private readonly experience$ = signal<ExperienceResponseV1 | null>(null);
  readonly experience = this.experience$.asReadonly();
  private readonly experienceCategory$ = signal<ExperienceCategoryResponseV1 | null>(null);
  readonly experienceCategory = this.experienceCategory$.asReadonly();
  private readonly experienceAnalyte$ = signal<ExperienceAnalyteResponseV1 | null>(null);
  readonly experienceAnalyte = this.experienceAnalyte$.asReadonly();

  constructor() {
    effect(() => {
      const userId = this.viewingUser()?.userId;
      untracked(() => {
        if (userId) {
          firstValueFrom(this.getHealthProfile(userId)).catch(() => {});
        }
      });
    });
  }

  getHealthProfile(userId: string): Observable<HealthProfile> {
    this.logService.info('[HealthService] getHealthProfile', { userId });
    return of(mockHealthProfile1).pipe(
      tap((healthProfile) => this.healthProfileMap$.set({ ...this.healthProfileMap$(), [userId]: healthProfile })),
      tap((healthProfile) => firstValueFrom(this.getExperience(healthProfile.youtopiaExperienceId)).catch(() => {}))
    );
    // return this.http.get<ServiceResponseV1<ExperienceResponseV1>>(`${this.healthApiUrl}`).pipe(
    //   map((response) => response.data),
    //   tap((experience) => {
    //     this.experiencesMap$.set({
    //       ...this.experiencesMap$(),
    //       [experienceId]: experience
    //     });
    //     this.experience$.set(experience);
    //   }),
    //   catchError((error) => this.handleError(error, this.translateService.instant('Health.Get_Experience_Error')))
    // );
  }

  getExperience(experienceId: string): Observable<ExperienceResponseV1> {
    this.logService.info('[HealthService] getExperience', { experienceId });
    return of(mockExperience1).pipe(
      tap((experience) => {
        this.experiencesMap$.set({
          ...this.experiencesMap$(),
          [experienceId]: experience
        });
        this.experience$.set(experience);
      })
    );
    // return this.http.get<ServiceResponseV1<ExperienceResponseV1>>(`${this.healthApiUrl}`).pipe(
    //   map((response) => response.data),
    //   tap((experience) => {
    //     this.experiencesMap$.set({
    //       ...this.experiencesMap$(),
    //       [experienceId]: experience
    //     });
    //     this.experience$.set(experience);
    //   }),
    //   catchError((error) => this.handleError(error, this.translateService.instant('Health.Get_Experience_Error')))
    // );
  }

  getExperienceCategory(
    experienceId: string,
    categoryId: string
  ): Observable<ExperienceCategoryResponseV1 | undefined> {
    this.logService.info('[HealthService] getExperienceCategory', { experienceId, categoryId });
    return of(mockExperienceCategoryDetails.find((c) => c.categoryId === categoryId)).pipe(
      tap((experienceCategory) => this.experienceCategory$.set(experienceCategory ?? null))
    );
  }

  getExperienceAnalyte(
    experienceId: string,
    categoryId: string,
    analyteId: string
  ): Observable<ExperienceAnalyteResponseV1 | undefined> {
    this.logService.info('[HealthService] getExperienceAnalyte', { experienceId, categoryId, analyteId });
    return of(mockExperienceCategoryDetails.find((c) => c.categoryId === categoryId)).pipe(
      map((experienceCategory) => experienceCategory?.analytes.find((a) => a.analyteId === analyteId)),
      tap((experienceAnalyte) => this.experienceAnalyte$.set(experienceAnalyte ?? null))
    );
  }

  // Capture the current form values as the baseline for the section about to be edited.
  snapshotHealthHistory(): void {
    this.healthHistorySnapshot$.set(structuredClone(this.healthHistoryFormModel$()));
  }

  // Restore the form to the last snapshot, discarding any edits made since the section was entered.
  revertHealthHistory(): void {
    const snapshot = this.healthHistorySnapshot$();
    if (snapshot) {
      this.healthHistoryFormModel$.set(structuredClone(snapshot));
    }
  }

  handleError(error: any, message: string) {
    const header = this.translateService.instant('Common.Something_Went_Wrong');
    this.logService.error('[HealthService] Error', { error, message });
    this.toastService.error(header, message);
    return throwError(() => error);
  }

  // Admin
  toggleStartingLineComplete(userId: string, complete: boolean) {
    const healthProfile = this.healthProfileMap$()[userId];
    if (healthProfile) {
      this.healthProfileMap$.set({
        ...this.healthProfileMap$(),
        [userId]: { ...healthProfile, startingLineComplete: complete }
      });
    }
  }

  toggleNOf1Enabled(userId: string, complete: boolean) {
    const healthProfile = this.healthProfileMap$()[userId];
    if (healthProfile) {
      this.healthProfileMap$.set({
        ...this.healthProfileMap$(),
        [userId]: { ...healthProfile, nOf1Enabled: complete }
      });
    }
  }
}
